"use client";

import {
  ImpromptuSettings,
  type ImpromptuSettingsProps,
} from "@/components/impromptu/ImpromptuSettings";
import type { ImpromptuAttemptPhase, ImpromptuRecoveryHandlers } from "@/hooks/useImpromptuAttempt";
import { IMPROMPTU_MAX_DURATION_SECONDS, IMPROMPTU_PROFILE_OPTIONS } from "@/lib/impromptu/config";
import { IMPROMPTU_COPY, IMPROMPTU_RECOVERY_ACTION_LABELS } from "@/lib/impromptu/copy";
import { formatClock } from "@/lib/impromptu/format";
import { Check, Square } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * Everything between pressing start and having a report: permission, recording,
 * analysis, and failure.
 *
 * The phase is a union, so each state renders from data that only exists in that
 * state — the countdown cannot show a stale number during analysis, and the
 * failure branch cannot render without a failure to render.
 */

const COPY = IMPROMPTU_COPY.attempt;

/**
 * Counts a rate limit down so the recovery button is not offered before it can
 * work. A burst limit tells the learner to wait a moment and then hands them a
 * button; pressing it immediately used to fail the same way again.
 *
 * Counted from a fixed deadline rather than by decrementing, so a backgrounded tab
 * whose timers stop firing does not hold the button for longer than the wait.
 * Returns 0 when there is nothing to wait for.
 */
function useRetryCountdown(seconds: number | undefined) {
  const [remaining, setRemaining] = useState(seconds ?? 0);

  useEffect(() => {
    setRemaining(seconds ?? 0);
    if (!seconds) return;

    const deadline = Date.now() + seconds * 1_000;
    const interval = window.setInterval(() => {
      const left = Math.ceil((deadline - Date.now()) / 1_000);
      setRemaining(left > 0 ? left : 0);
      if (left <= 0) window.clearInterval(interval);
    }, 500);

    return () => window.clearInterval(interval);
  }, [seconds]);

  return remaining;
}

type ImpromptuAttemptViewProps = {
  phase: ImpromptuAttemptPhase;
  promptText: string;
  languageName: string;
  settings: Omit<ImpromptuSettingsProps, "surface" | "className" | "disabled">;
  /** True once the microphone is open: switching language mid-attempt would send
   * the recording for transcription against a language it is not in. */
  settingsLocked: boolean;
  onStop: () => void;
  onCancel: () => void;
  recovery: ImpromptuRecoveryHandlers;
  surface?: "light" | "dark";
};

export function ImpromptuAttemptView({
  phase,
  promptText,
  languageName,
  settings,
  settingsLocked,
  onStop,
  onCancel,
  recovery,
  surface = "dark",
}: ImpromptuAttemptViewProps) {
  const retryIn = useRetryCountdown(
    phase.kind === "failed" ? phase.failure.retryAfterSeconds : undefined
  );
  const isLight = surface === "light";
  const heading = isLight ? "text-[#1C1C1E]" : "text-white";
  const body = isLight ? "text-[#48484A]" : "text-[#AEAEB2]";
  const muted = isLight ? "text-[#6C6C70]" : "text-[#8E8E93]";
  const divider = isLight ? "border-[#D1D1D6]" : "border-white/10";
  const quietButton = `inline-flex min-h-11 items-center gap-2 rounded-lg px-4 text-sm font-bold transition-colors active:translate-y-px focus-visible:outline-none focus-visible:ring-2 ${
    isLight
      ? "text-[#48484A] hover:bg-black/[0.04] hover:text-[#1C1C1E] focus-visible:ring-[#A8431F]"
      : "text-[#AEAEB2] hover:bg-white/[0.06] hover:text-white focus-visible:ring-[#FD954F]"
  }`;
  const outlineButton = `min-h-11 rounded-lg border px-5 text-sm font-bold transition-colors active:translate-y-px focus-visible:outline-none focus-visible:ring-2 ${
    isLight
      ? "border-[#C7C7CC] bg-white text-[#1C1C1E] hover:border-[#A8431F] focus-visible:ring-[#A8431F]"
      : "border-white/10 text-white hover:bg-white/[0.06] focus-visible:ring-[#FD954F]"
  }`;
  const primaryButton =
    "min-h-11 rounded-lg bg-[#FD954F] px-5 text-sm font-bold text-[#1C1C1E] transition-colors hover:bg-[#F28747] active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A8431F]";
  const profileLabel =
    IMPROMPTU_PROFILE_OPTIONS.find((profile) => profile.value === settings.learnerProfile)?.label ??
    settings.learnerProfile;

  return (
    <div className="mx-auto grid w-full max-w-7xl gap-10 lg:grid-cols-[minmax(0,1.35fr)_minmax(17rem,0.65fr)] lg:gap-20">
      <section className="min-w-0">
        {/* The prompt is the heading. It used to sit third, under an eyebrow, a
            headline and a subheading about the feature — so the largest text on
            screen during a recording was a pitch for the thing being used, and
            the one sentence the learner had to answer was set smaller than it. */}
        <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#A8431F]">
          {COPY.promptLabel}
        </p>
        <h1
          className={`mt-4 max-w-[25ch] text-[2rem] font-extrabold leading-[1.08] sm:text-[2.75rem] lg:text-[3.25rem] ${heading}`}
        >
          {promptText}
        </h1>

        {phase.kind === "requesting_microphone" && (
          <div className={`mt-10 border-t pt-7 ${divider}`} role="status" aria-live="polite">
            <p className={`text-xl font-bold ${heading}`}>{COPY.requestingMicrophone}</p>
            <p className={`mt-2 text-sm ${body}`}>{COPY.requestingMicrophoneHint}</p>
          </div>
        )}

        {phase.kind === "recording" && (
          <div className={`mt-10 border-t pt-7 ${divider}`} role="status" aria-live="polite">
            <div className="flex items-end gap-4">
              <span className={`font-mono text-5xl font-bold tabular-nums sm:text-6xl ${heading}`}>
                {formatClock(phase.elapsedSeconds)}
              </span>
              <span className={`pb-2 text-sm font-bold ${muted}`}>
                {formatClock(IMPROMPTU_MAX_DURATION_SECONDS - phase.elapsedSeconds)}{" "}
                {COPY.timeRemainingSuffix}
              </span>
            </div>
            <p className={`mt-3 text-sm ${body}`}>{COPY.recordingHint}</p>
            <button
              type="button"
              onClick={onStop}
              className="mt-6 inline-flex min-h-14 w-full items-center justify-center gap-3 rounded-lg bg-[#FD954F] px-6 text-base font-bold text-[#1C1C1E] transition-colors hover:bg-[#F28747] active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A8431F] sm:w-auto sm:min-w-64"
            >
              <Square className="h-4 w-4 fill-current" aria-hidden="true" />
              {COPY.stopAndAnalyse}
            </button>
          </div>
        )}

        {phase.kind === "analysing" && (
          <div
            className={`mt-10 border-t pt-7 ${divider}`}
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <div className="flex items-center gap-3">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full bg-[#FD954F] motion-safe:animate-pulse"
                aria-hidden="true"
              />
              <p className={`text-xl font-bold ${heading}`}>{COPY.analysingHeading}</p>
            </div>
            <p className={`mt-3 max-w-xl text-base leading-7 ${body}`}>{COPY.analysingHint}</p>
            <button type="button" onClick={onCancel} className={`mt-5 ${quietButton}`}>
              {COPY.cancel}
            </button>
          </div>
        )}

        {phase.kind === "failed" && (
          <div className="mt-10 border-l-2 border-[#FD954F] pl-5" role="alert">
            <h2 className={`text-xl font-bold ${heading}`}>{COPY.failureHeading}</h2>
            <p className={`mt-2 max-w-xl text-sm leading-6 ${body}`}>{phase.failure.message}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {/* The offer matches what actually failed. A dropped upload asks to
                  send the recording we still hold; only a lost recording asks
                  someone to speak for a minute again. */}
              {phase.failure.recovery !== "none" && (
                <button
                  type="button"
                  onClick={recovery[phase.failure.recovery]}
                  disabled={retryIn > 0}
                  aria-label={
                    retryIn > 0
                      ? COPY.retryHeldLabel(
                          IMPROMPTU_RECOVERY_ACTION_LABELS[phase.failure.recovery]
                        )
                      : undefined
                  }
                  className={`${primaryButton} disabled:cursor-not-allowed disabled:bg-[#FD954F]/40 disabled:text-[#1C1C1E]/60 disabled:hover:bg-[#FD954F]/40`}
                >
                  {retryIn > 0
                    ? COPY.retryAvailableIn(retryIn)
                    : IMPROMPTU_RECOVERY_ACTION_LABELS[phase.failure.recovery]}
                </button>
              )}
              {/* Always present, including when nothing can be retried, so a
                  failure is never a dead end. */}
              <button type="button" onClick={onCancel} className={outlineButton}>
                {COPY.backToSetup}
              </button>
            </div>
          </div>
        )}
      </section>

      <aside className={`border-t pt-7 lg:border-l lg:border-t-0 lg:pl-9 lg:pt-1 ${divider}`}>
        {settingsLocked ? (
          <>
            <h2 className={`text-lg font-extrabold ${heading}`}>{COPY.detailsHeading}</h2>
            <dl className="mt-5 space-y-5">
              <div>
                <dt className={`text-xs font-bold uppercase tracking-[0.1em] ${muted}`}>
                  {COPY.languageDetailLabel}
                </dt>
                <dd className={`mt-1.5 text-base font-bold ${heading}`}>{languageName}</dd>
              </div>
              <div>
                <dt className={`text-xs font-bold uppercase tracking-[0.1em] ${muted}`}>
                  {COPY.levelDetailLabel}
                </dt>
                <dd className={`mt-1.5 text-base font-bold ${heading}`}>{profileLabel}</dd>
              </div>
            </dl>
          </>
        ) : (
          <>
            <h2 className={`text-lg font-extrabold ${heading}`}>{COPY.adjustHeading}</h2>
            <ImpromptuSettings
              {...settings}
              surface={surface}
              disabled={settingsLocked}
              className="mt-5"
            />
          </>
        )}

        {phase.kind === "requesting_microphone" && (
          <div className={`mt-7 space-y-4 border-t pt-6 text-sm ${divider} ${body}`}>
            {COPY.checklist(IMPROMPTU_MAX_DURATION_SECONDS).map((item) => (
              <div key={item} className="flex gap-3">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#FD954F]" aria-hidden="true" />
                <p>{item}</p>
              </div>
            ))}
          </div>
        )}
      </aside>
    </div>
  );
}
